import React, { useState, useEffect, useMemo } from 'react'
import { useDataQuery, useDataEngine } from '@dhis2/app-runtime'
import { Card, LinearLoader, Button, AlertBar } from '@dhis2/ui'
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { CONFIG } from '../../leadership/api/LeadershipServices';

const LeadershipDashboard = ({ orgUnitId, accessibleOrgUnitIds = [], filterScope = 'all' }) => {
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [competencies, setCompetencies] = useState([])
  const [viewMode, setViewMode] = useState('aggregate')
  const [loadingCompetencies, setLoadingCompetencies] = useState(true) 
  const [processedData, setProcessedData] = useState([]) 
  const [processing, setProcessing] = useState(false) 
  const [rawEvents, setRawEvents] = useState([])
  const engine = useDataEngine()

  const [notification, setNotification] = useState(null);

  // Load competency configuration
  useEffect(() => {
    const loadCompetencyConfig = async () => {
      if (!orgUnitId) return
      
      setLoadingCompetencies(true)
      try {
        const query = {
          configEvents: {
            resource: 'events',
            params: {
              program: CONFIG.program,
              programStage: CONFIG.stages.config,
              orgUnit: orgUnitId,
              ouMode: 'ACCESSIBLE',
              fields: 'event,dataValues[dataElement,value]',
              pageSize: 50,
            },
          },
        }
        
        const result = await engine.query(query)
        const events = result.configEvents?.events || []
        
        const loadedCompetencies = events.map(event => {
          const codeDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyCode)
          const defDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.competencyDefinition)
          const weightDV = event.dataValues.find(dv => dv.dataElement === CONFIG.de.weightValue)
          
          return {
            competencyId: event.event,
            name: codeDV?.value || '',
            definition: defDV?.value || codeDV?.value || '',
            weight: weightDV ? parseFloat(weightDV.value) : 0.25,
          }
        }).filter(c => c.name.trim())
        
        // If no competencies found in config, use hardcoded ones based on the data we see
        if (loadedCompetencies.length === 0) {
          console.log('No competencies in config, using hardcoded defaults')
          setCompetencies([
            { competencyId: 'respect', name: 'RESPECT', weight: 0.25 },
            { competencyId: 'excellence', name: 'EXCELLENCE', weight: 0.25 },
            { competencyId: 'learning', name: 'LEARNING', weight: 0.25 },
            { competencyId: 'integrity', name: 'INTEGRITY', weight: 0.25 },
          ])
        } else {
          setCompetencies(loadedCompetencies)
        }
        
        console.log('Loaded competencies:', loadedCompetencies)
      } catch (error) {
        console.error('Failed to load competency config:', error)
        // Fallback to hardcoded competencies
        setCompetencies([
          { competencyId: 'respect', name: 'RESPECT', weight: 0.25 },
          { competencyId: 'excellence', name: 'EXCELLENCE', weight: 0.25 },
          { competencyId: 'learning', name: 'LEARNING', weight: 0.25 },
          { competencyId: 'integrity', name: 'INTEGRITY', weight: 0.25 },
        ])
      } finally {
        setLoadingCompetencies(false)
      }
    }
    
    loadCompetencyConfig()
  }, [orgUnitId, engine])

  const eventsQuery = useMemo(() => ({
    eventsData: {
      resource: 'events',
      params: {
        program: CONFIG.program,
        orgUnit: orgUnitId,
        ouMode: 'DESCENDANTS',
        fields: 'event,programStage,eventDate,orgUnit,orgUnitName,trackedEntityInstance,dataValues[dataElement,value]',
        pageSize: 1000,
        order: 'eventDate:desc'
      }
    }
  }), [orgUnitId])

  const { loading: eventsLoading, error: eventsError, data: eventsData } = useDataQuery(eventsQuery)

  // Process data when all dependencies are loaded
  useEffect(() => {
    if (eventsLoading || loadingCompetencies) {
      return
    }

    if (!eventsData?.eventsData?.events) {
      console.log('Missing required data for processing')
      setProcessedData([])
      return
    }

    setProcessing(true)
    console.log('=== DEBUG: Processing Real Data ===')
    console.log('Competencies available:', competencies.length)
    
    const events = (eventsData.eventsData.events || []).filter(event =>
      filterScope === 'all' ||
      accessibleOrgUnitIds.length === 0 ||
      accessibleOrgUnitIds.includes(event.orgUnit)
    )
    console.log('Events found:', events.length)
    
    // Store raw events for debugging
    setRawEvents(events)

    // Group events by program stage and look for rating values
    const stageGroups = {}
    const ratingValues = []

    events.forEach((event, index) => {
      const stage = event.programStage
      if (!stageGroups[stage]) {
        stageGroups[stage] = []
      }
      stageGroups[stage].push(event)

      // Look for any numeric values that could be ratings (1-5)
      event.dataValues?.forEach(dv => {
        const num = parseInt(dv.value)
        if (!isNaN(num) && num >= 1 && num <= 5) {
          ratingValues.push({
            eventId: event.event,
            stage: stage,
            dataElement: dv.dataElement,
            value: num,
            orgUnit: event.orgUnitName
          })
        }
      })
    })

    console.log('📊 Events by stage:', Object.keys(stageGroups).map(stage => ({
      stage,
      count: stageGroups[stage].length
    })))

    console.log('🎯 Rating values found:', ratingValues.length)
    if (ratingValues.length > 0) {
      console.log('Sample ratings:', ratingValues.slice(0, 10))
    }

    // If we have ratings, create staff entries
    if (ratingValues.length > 0) {
      // Group ratings by event to see if they belong together
      const ratingsByEvent = {}
      ratingValues.forEach(rating => {
        if (!ratingsByEvent[rating.eventId]) {
          ratingsByEvent[rating.eventId] = []
        }
        ratingsByEvent[rating.eventId].push(rating)
      })

      console.log('📊 Events with ratings:', Object.keys(ratingsByEvent).length)

      // Create a staff entry for each event that has ratings
      const staffEntries = Object.keys(ratingsByEvent).map((eventId, index) => {
        const eventRatings = ratingsByEvent[eventId]
        const event = events.find(e => e.event === eventId)
        
        // Create a staff member for this event
        const staffId = `staff-${index}`
        const rawScores = {}
        
        // If we have 4 ratings, assign them to the 4 competencies in order
        if (eventRatings.length >= 4) {
          const competenciesList = ['RESPECT', 'EXCELLENCE', 'LEARNING', 'INTEGRITY']
          competenciesList.forEach((compName, i) => {
            if (i < eventRatings.length) {
              // Find the competency ID that matches this name
              const comp = competencies.find(c => c.name === compName)
              if (comp) {
                rawScores[comp.competencyId] = eventRatings[i].value
              }
            }
          })
        } else {
          // Otherwise just assign to the first competency
          eventRatings.forEach((rating, i) => {
            if (i < competencies.length) {
              rawScores[competencies[i].competencyId] = rating.value
            }
          })
        }

        return {
          id: staffId,
          name: `Staff Member ${index + 1} (${event?.orgUnitName || 'Unknown'})`,
          position: 'Staff',
          rawScores,
          reviews: [{
            id: eventId,
            reviewType: event?.programStage === CONFIG.stages.midYear ? 'Mid-Year' : 
                        event?.programStage === CONFIG.stages.endYear ? 'Year-End' : 'Unknown',
            date: event?.eventDate || new Date().toISOString(),
            orgUnit: event?.orgUnitName || 'Unknown',
            rawScores: { ...rawScores }
          }],
          latestReview: {
            id: eventId,
            reviewType: event?.programStage === CONFIG.stages.midYear ? 'Mid-Year' : 
                        event?.programStage === CONFIG.stages.endYear ? 'Year-End' : 'Unknown',
            date: event?.eventDate || new Date().toISOString(),
            orgUnit: event?.orgUnitName || 'Unknown',
            rawScores: { ...rawScores }
          },
          weights: {}
        }
      })

      // Calculate final scores
      const result = staffEntries.map(staff => {
        const latest = staff.latestReview
        const scores = {}
        let totalScore = 0

        competencies.forEach(comp => {
          const compId = comp.competencyId
          const rawScore = latest.rawScores[compId] || 0
          const weightedScore = rawScore * comp.weight
          
          scores[compId] = {
            name: comp.name,
            raw: rawScore,
            weighted: parseFloat(weightedScore.toFixed(2))
          }
          
          totalScore += weightedScore
        })

        return {
          ...staff,
          totalScore: parseFloat(totalScore.toFixed(2)),
          lastReview: latest.date,
          reviewType: latest.reviewType,
          scores: scores,
        }
      })

      console.log('Processed real data result:', result)
      console.log('Staff with reviews:', result.length)
      setProcessedData(result)
    } else {
      console.log('No rating values found in events')
      setProcessedData([])
    }

    setProcessing(false)

  }, [eventsData, competencies, eventsLoading, loadingCompetencies, accessibleOrgUnitIds, filterScope])

  // Loading state
  if (eventsLoading || loadingCompetencies || processing) {
    return (
      <Card style={{ padding: 16 }}>
        <h3>Leadership & Management Dashboard</h3>
        <LinearLoader amount={50} />
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          Loading leadership performance data...
        </div>
      </Card>
    )
  }

  const hasRealData = processedData.length > 0

  // If no data is available, show empty state with debug info
  if (!hasRealData) {
    // Count events by type for debugging
    const events = (eventsData?.eventsData?.events || []).filter(event =>
      filterScope === 'all' ||
      accessibleOrgUnitIds.length === 0 ||
      accessibleOrgUnitIds.includes(event.orgUnit)
    )
    const midYearEvents = events.filter(e => e.programStage === CONFIG.stages.midYear).length
    const endYearEvents = events.filter(e => e.programStage === CONFIG.stages.endYear).length
    const weightEvents = events.filter(e => e.programStage === CONFIG.stages.weights).length
    
    // Find potential rating data elements
    const ratingDataElements = new Set()
    events.forEach(event => {
      event.dataValues?.forEach(dv => {
        const num = parseInt(dv.value)
        if (!isNaN(num) && num >= 1 && num <= 5) {
          ratingDataElements.add(dv.dataElement)
        }
      })
    })

    return (
      <Card style={{ padding: 16, marginBottom: 24 }}>
        <AlertBar warning>
          {competencies.length > 0 
            ? `Found ${events.length} leadership events but no rating values detected.`
            : 'No competencies configured. Please set up competencies first.'}
        </AlertBar>

        <Card style={{ padding: 40, textAlign: 'center', marginTop: 24 }}>
          <div style={{ fontSize: 48, color: '#e9ecef', marginBottom: 16 }}>📊</div>
          <h4 style={{ color: '#6c757d', marginBottom: 8 }}>Leadership Data Debug Info</h4>
          <p style={{ color: '#6c757d', marginBottom: 16, textAlign: 'left' }}>
            <strong>Events Found:</strong> {events.length}<br/>
            <strong>Mid-Year Reviews:</strong> {midYearEvents}<br/>
            <strong>End-Year Reviews:</strong> {endYearEvents}<br/>
            <strong>Weight Configurations:</strong> {weightEvents}<br/>
            <strong>Competencies Configured:</strong> {competencies.length}<br/>
            <strong>Rating Data Elements Found:</strong> {Array.from(ratingDataElements).join(', ') || 'None'}<br/>
          </p>
          <div style={{ fontSize: 14, color: '#6c757d' }}>
            Expected rating data element: <code>{CONFIG.de.ratingScore}</code><br/>
            If you're seeing different data elements, the leadership data might be stored differently.
          </div>
        </Card>
      </Card>
    )
  }

  const aggregateScores = calculateAggregateScores(processedData, competencies)
  const averageWeights = calculateAverageWeights(processedData, competencies)

  // If staff is selected, show individual view
  if (selectedStaff && viewMode === 'individual') {
    return (
      <IndividualLeadershipView
        staff={selectedStaff}
        competencies={competencies}
        onBack={() => {
          setSelectedStaff(null)
          setViewMode('aggregate')
        }}
      />
    )
  }

  // Progress Bar Component
  const ProgressBar = ({ value, height = '6px', borderRadius = '3px', color = '#007bff' }) => (
    <div style={{
      width: '100%',
      height,
      backgroundColor: '#e5e7eb',
      borderRadius
    }}>
      <div style={{
        width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
        height: '100%',
        backgroundColor: color,
        borderRadius,
        transition: 'width 0.3s ease'
      }} />
    </div>
  )

  // Prepare radar chart data
  const radarData = competencies.map(comp => {
    const compId = comp.competencyId
    const avgScore = parseFloat(aggregateScores[compId]?.average || 0)
    return {
      subject: comp.name.length > 15 ? comp.name.substring(0, 15) + '...' : comp.name,
      fullSubject: comp.name,
      score: (avgScore / 5) * 100, // Convert to percentage (5 is max score)
      fullMark: 100,
      weight: averageWeights[compId] ? (averageWeights[compId] * 100).toFixed(0) : 'N/A',
      averageScore: avgScore.toFixed(2)
    }
  })

  const clearNotification = () => {
        setNotification(null);
  };

  return (
    <Card style={{ padding: 16, marginBottom: 24 }}>
      <AlertBar success duration={5000} onHidden={clearNotification}>
        {`Showing leadership performance data for ${processedData.length} staff members across ${competencies.length} competencies`}
      </AlertBar>

      {/* Overall Score Card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        <Card style={{
          padding: 20,
          backgroundColor: '#007bff',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 14, opacity: 0.9 }}>Company Average Score</div>
          <div style={{ fontSize: 48, fontWeight: 'bold', margin: '8px 0' }}>
            {aggregateScores.overall.average}
          </div>
          <div style={{ fontSize: 14, opacity: 0.9 }}>
            Average total score across {processedData.length} staff members (Max: 5.00)
          </div>
        </Card>
      </div>

      {/* Radar Chart and Score Breakdown */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 24,
        marginBottom: 24,
        '@media (minWidth: 768px)': {
          gridTemplateColumns: '1fr 1fr'
        }
      }}>
        {/* Radar Chart */}
        {competencies.length > 0 && (
          <div>
            <h4 style={{ marginBottom: 16 }}>Overall Company Competency Averages</h4>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar
                    name="Average Score (%)"
                    dataKey="score"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                  />
                  <Tooltip
                    formatter={(value, name, props) => {
                      if (name === 'score') {
                        return [
                          `${value}% (Avg: ${props.payload.averageScore})`,
                          'Score'
                        ]
                      }
                      return value
                    }}
                    labelFormatter={(value, payload) => {
                      if (payload && payload[0]) {
                        return `${payload[0].payload.fullSubject} (Weight: ${payload[0].payload.weight}%)`
                      }
                      return value
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Score Breakdown */}
        <div>
          <h4 style={{ marginBottom: 16 }}>Overall Company Average Scores</h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 12,
            marginBottom: 16
          }}>
            {competencies.map((comp, index) => {
              const compId = comp.competencyId
              const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14']
              const color = colors[index % colors.length]
              
              return (
                <Card key={compId} style={{ padding: 12, borderLeft: `4px solid ${color}` }}>
                  <div style={{ fontWeight: '600', color: color, fontSize: 14 }}>
                    {comp.name.length > 12 ? comp.name.substring(0, 12) + '...' : comp.name}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 'bold', margin: '8px 0' }}>
                    {aggregateScores[compId]?.average || '0.00'}
                  </div>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>
                    Weight: {averageWeights[compId] ? (averageWeights[compId] * 100).toFixed(0) + '%' : 'N/A'}
                  </div>
                </Card>
              )
            })}
          </div>
        </div>
      </div>

      {/* Staff Performance - Responsive Div-based Layout */}
      <div style={{ marginTop: 24 }}>
        <h4 style={{ marginBottom: 16 }}>Staff Performance Details</h4>
        <Card style={{ padding: 16, overflowX: 'auto' }}>
          {/* Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `minmax(150px, 1fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) ${competencies.map(() => 'minmax(100px, 0.8fr)').join(' ')} minmax(80px, 0.5fr) minmax(100px, 0.5fr)`,
            gap: '12px',
            padding: '12px 16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '12px',
            fontWeight: '600',
            fontSize: '14px',
            minWidth: `${800 + (competencies.length * 100)}px`
          }}>
            <div>Staff Name</div>
            <div>Position</div>
            <div>Last Review</div>
            <div>Review Type</div>
            {competencies.map(comp => (
              <div key={comp.competencyId} title={comp.name}>
                {comp.name.length > 10 ? comp.name.substring(0, 10) + '...' : comp.name}
              </div>
            ))}
            <div>Total Score</div>
            <div>Actions</div>
          </div>

          {/* Staff Items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {processedData.map((staff) => {
              const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14']
              
              return (
                <div key={staff.id} style={{
                  display: 'grid',
                  gridTemplateColumns: `minmax(150px, 1fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) ${competencies.map(() => 'minmax(100px, 0.8fr)').join(' ')} minmax(80px, 0.5fr) minmax(100px, 0.5fr)`,
                  gap: '12px',
                  padding: '16px',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  alignItems: 'center',
                  minWidth: `${800 + (competencies.length * 100)}px`
                }}>
                  {/* Staff Name */}
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>
                    {staff.name}
                  </div>

                  {/* Position */}
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    {staff.position}
                  </div>

                  {/* Last Review */}
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    {staff.lastReview ? new Date(staff.lastReview).toLocaleDateString() : 'N/A'}
                  </div>

                  {/* Review Type */}
                  <div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      backgroundColor: staff.reviewType === 'Mid-Year' ? '#fff3cd' : '#d1ecf1',
                      color: staff.reviewType === 'Mid-Year' ? '#856404' : '#0c5460',
                      whiteSpace: 'nowrap'
                    }}>
                      {staff.reviewType || 'Unknown'}
                    </span>
                  </div>

                  {/* Competency Scores */}
                  {competencies.map((comp, index) => {
                    const compId = comp.competencyId
                    const score = staff.scores[compId] || { raw: 0, weighted: 0 }
                    const color = colors[index % colors.length]
                    
                    return (
                      <div key={compId} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          {score.weighted.toFixed(2)}
                        </div>
                        <ProgressBar
                          value={(score.raw / 5) * 100}
                          height="4px"
                          color={color}
                        />
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                          Raw: {score.raw}/5
                        </div>
                      </div>
                    )
                  })}

                  {/* Total Score */}
                  <div style={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: staff.totalScore >= 4 ? '#28a745' : staff.totalScore >= 3 ? '#ffc107' : '#dc3545',
                    textAlign: 'center'
                  }}>
                    {staff.totalScore.toFixed(2)}
                  </div>

                  {/* Actions */}
                  <div>
                    <button
                      
                      onClick={() => {
                        setSelectedStaff(staff)
                        setViewMode('individual')
                      }}
                      style={{ width: '100%', 
                               backgroundColor: 'rgb(60, 57, 250)',
                               color: '#fff',
                               borderRadius: '8px',
                               cursor: 'pointer',
                               border: 'none',
                               boxShadow: "0 8px 10px rgba(40, 18, 18, 0.2)",
                               height: '35px',
                                 }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      </div>
    </Card>
  )
}

// Helper functions moved outside component
const calculateAggregateScores = (staffList, competencies) => {
  const staffCount = staffList.length
  if (staffCount === 0) {
    return {
      overall: { total: 0, average: 0 }
    }
  }

  const aggregate = {
    overall: { total: 0, average: 0 }
  }

  // Initialize competency aggregates
  competencies.forEach(comp => {
    aggregate[comp.competencyId] = { 
      total: 0, 
      average: 0,
      name: comp.name 
    }
  })

  staffList.forEach(staff => {
    aggregate.overall.total += staff.totalScore
    
    // Sum up each competency score
    competencies.forEach(comp => {
      const compId = comp.competencyId
      if (staff.scores[compId]) {
        aggregate[compId].total += staff.scores[compId].weighted
      }
    })
  })

  // Calculate averages
  aggregate.overall.average = (aggregate.overall.total / staffCount).toFixed(2)
  
  competencies.forEach(comp => {
    const compId = comp.competencyId
    if (aggregate[compId]) {
      aggregate[compId].average = (aggregate[compId].total / staffCount).toFixed(2)
    }
  })

  return aggregate
}

const calculateAverageWeights = (staffList, competencies) => {
  if (staffList.length === 0) {
    const defaultWeight = competencies.length > 0 ? (1 / competencies.length) : 0.25
    const defaultWeights = {}
    competencies.forEach(comp => {
      defaultWeights[comp.competencyId] = defaultWeight
    })
    return defaultWeights
  }

  const totalWeights = {}
  const weightCounts = {}

  // Initialize
  competencies.forEach(comp => {
    totalWeights[comp.competencyId] = 0
    weightCounts[comp.competencyId] = 0
  })

  // Sum weights (none in this simplified version)
  // Just return default weights
  const averageWeights = {}
  competencies.forEach(comp => {
    averageWeights[comp.competencyId] = comp.weight || (1 / competencies.length)
  })

  return averageWeights
}

// IndividualLeadershipView component
const IndividualLeadershipView = ({ staff, competencies, onBack }) => {
  const ProgressBar = ({ value, height = '6px', borderRadius = '3px', color = '#007bff' }) => (
    <div style={{
      width: '100%',
      height,
      backgroundColor: '#e5e7eb',
      borderRadius
    }}>
      <div style={{
        width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
        height: '100%',
        backgroundColor: color,
        borderRadius,
        transition: 'width 0.3s ease'
      }} />
    </div>
  )

  // Prepare radar chart data for individual
  const radarData = competencies.map((comp, index) => {
    const compId = comp.competencyId
    const score = staff.scores[compId] || { raw: 0, weighted: 0 }
    const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14']
    
    return {
      subject: comp.name.length > 15 ? comp.name.substring(0, 15) + '...' : comp.name,
      fullSubject: comp.name,
      score: (score.raw / 5) * 100, // Convert to percentage
      fullMark: 100,
      weight: comp.weight ? (comp.weight * 100).toFixed(0) : 'N/A',
      rawScore: score.raw,
      weightedScore: score.weighted,
      color: colors[index % colors.length]
    }
  })

  return (
    <Card style={{ padding: 16, marginBottom: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, '@media (min-width: 768px)': { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' } }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Button primary small onClick={onBack}>
              ← Back to Overall Company View
            </Button>
            <h3 style={{ margin: 0 }}>{staff.name} - Leadership Performance</h3>
          </div>
          <div style={{ fontSize: 14, color: '#6c757d' }}>
            {staff.position} • Last Review: {staff.lastReview ? new Date(staff.lastReview).toLocaleDateString() : 'N/A'} • {staff.reviewType}
          </div>
        </div>
      </div>

      {/* Individual Score Card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        <Card style={{
          padding: 20,
          backgroundColor: '#28a745',
          color: 'white',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 14, opacity: 0.9 }}>Individual Total Score</div>
          <div style={{ fontSize: 48, fontWeight: 'bold', margin: '8px 0' }}>
            {staff.totalScore.toFixed(2)}
          </div>
          <div style={{ fontSize: 14, opacity: 0.9 }}>
            Based on weighted competency scores (Max: 5.00)
          </div>
        </Card>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 24,
        marginBottom: 24,
        '@media (min-width: 768px)': {
          gridTemplateColumns: '1fr 1fr'
        }
      }}>
        {/* Radar Chart */}
        {competencies.length > 0 && (
          <div>
            <h4 style={{ marginBottom: 16 }}>Competency Scores</h4>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar
                    name="Score (%)"
                    dataKey="score"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                  />
                  <Tooltip
                    formatter={(value, name, props) => {
                      if (name === 'score') {
                        return [
                          `${value}% (${props.payload.rawScore}/5)`,
                          'Score'
                        ]
                      }
                      return value
                    }}
                    labelFormatter={(value, payload) => {
                      if (payload && payload[0]) {
                        return `${payload[0].payload.fullSubject} (Weight: ${payload[0].payload.weight}%)`
                      }
                      return value
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Detailed Score Breakdown */}
        <div>
          <h4 style={{ marginBottom: 16 }}>Detailed Score Breakdown</h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 12,
            marginBottom: 16
          }}>
            {competencies.map((comp, index) => {
              const compId = comp.competencyId
              const score = staff.scores[compId] || { raw: 0, weighted: 0 }
              const colors = ['#e83e8c', '#ffc107', '#20c997', '#17a2b8', '#6f42c1', '#fd7e14']
              const color = colors[index % colors.length]
              
              return (
                <Card key={compId} style={{ padding: 12, borderLeft: `4px solid ${color}` }}>
                  <div style={{ fontWeight: '600', color: color, fontSize: 14 }}>
                    {comp.name.length > 12 ? comp.name.substring(0, 12) + '...' : comp.name}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 'bold', margin: '8px 0' }}>
                    {score.weighted.toFixed(2)}
                  </div>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>
                    Raw: {score.raw}/5 • Weight: {comp.weight ? (comp.weight * 100).toFixed(0) + '%' : 'N/A'}
                  </div>
                  <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                    {((score.raw / 5) * 100).toFixed(1)}% of possible
                  </div>
                </Card>
              )
            })}
          </div>
        </div>
      </div>
    </Card>
  )
}

export default LeadershipDashboard
